import { fetchWithAuth, type AccessTokenGetter } from "@/lib/utils";

export interface CostRange {
  timezone: "UTC";
  startDate: string;
  endDateExclusive: string;
  days: number;
}

export interface CostDailyRow {
  date: string;
  costUsd: number;
  entryCount: number;
}

export interface CostUserRow {
  userId: string;
  totalCostUsd: number;
  entryCount: number;
  unknownCostCount: number;
}

export interface UserCostSummary {
  contractVersion: 1;
  scope: "user";
  userId: string;
  range: CostRange;
  daily: CostDailyRow[];
  totalCostUsd: number;
  currency: "USD";
  unknownCostCount: number;
  unattributedCostCount: number;
}

export interface TenantCostSummary {
  contractVersion: 1;
  scope: "tenant";
  range: CostRange;
  daily: CostDailyRow[];
  totalCostUsd: number;
  currency: "USD";
  unknownCostCount: number;
  unattributedCostCount: number;
  userCount: number;
  users: CostUserRow[];
}

export type CostSummary = UserCostSummary | TenantCostSummary;

export class CostApiError extends Error {
  readonly status: number;
  readonly code?: string;

  constructor(message: string, status: number, code?: string) {
    super(message);
    this.name = "CostApiError";
    this.status = status;
    this.code = code;
  }
}

export const COSTS_UNSUPPORTED_CODE = "costs_unsupported";

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function isFiniteNumber(value: unknown): value is number {
  return typeof value === "number" && Number.isFinite(value);
}

function isNonNegativeInteger(value: unknown): value is number {
  return typeof value === "number" && Number.isSafeInteger(value) && value >= 0;
}

function isDateString(value: unknown): value is string {
  return typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value);
}

function addDays(date: string, days: number): string {
  const parsed = new Date(`${date}T00:00:00Z`);
  if (Number.isNaN(parsed.getTime())) return "";
  parsed.setUTCDate(parsed.getUTCDate() + days);
  return parsed.toISOString().slice(0, 10);
}

function invalidResponse(message: string): CostApiError {
  return new CostApiError(message, 200, "costs_invalid_response");
}

function validateRange(value: unknown): value is CostRange {
  if (!isRecord(value)) return false;
  if (
    value.timezone !== "UTC"
    || !isDateString(value.startDate)
    || !isDateString(value.endDateExclusive)
    || !isNonNegativeInteger(value.days)
    || value.days < 1
    || value.days > 90
    || value.endDateExclusive !== addDays(value.startDate, value.days)
  ) {
    return false;
  }
  return true;
}

function validateDaily(value: unknown, range: CostRange): value is CostDailyRow[] {
  if (!Array.isArray(value) || value.length !== range.days) return false;
  return value.every((row, index) => {
    if (!isRecord(row)) return false;
    return (
      row.date === addDays(range.startDate, index)
      && isFiniteNumber(row.costUsd)
      && isNonNegativeInteger(row.entryCount)
    );
  });
}

function validateCommon(value: unknown, scope: "user" | "tenant"): value is Record<string, unknown> {
  if (!isRecord(value)) return false;
  return (
    value.contractVersion === 1
    && value.scope === scope
    && validateRange(value.range)
    && validateDaily(value.daily, value.range)
    && isFiniteNumber(value.totalCostUsd)
    && value.currency === "USD"
    && isNonNegativeInteger(value.unknownCostCount)
    && isNonNegativeInteger(value.unattributedCostCount)
  );
}

function validateUserSummary(value: unknown): value is UserCostSummary {
  return (
    validateCommon(value, "user")
    && typeof value.userId === "string"
    && value.userId.length > 0
  );
}

function validateTenantSummary(value: unknown): value is TenantCostSummary {
  if (!validateCommon(value, "tenant")) return false;
  if (!isNonNegativeInteger(value.userCount) || !Array.isArray(value.users)) return false;
  if (value.users.length !== value.userCount) return false;
  return value.users.every((row) => (
    isRecord(row)
    && typeof row.userId === "string"
    && row.userId.length > 0
    && isFiniteNumber(row.totalCostUsd)
    && isNonNegativeInteger(row.entryCount)
    && isNonNegativeInteger(row.unknownCostCount)
  ));
}

function errorMessage(status: number, body: unknown): { message: string; code?: string } {
  if (isRecord(body)) {
    return {
      message: typeof body.error === "string" ? body.error : `Cost request failed (HTTP ${status})`,
      code: typeof body.code === "string" ? body.code : undefined,
    };
  }
  return { message: `Cost request failed (HTTP ${status})` };
}

async function readJson(response: Response): Promise<unknown> {
  return response.json().catch(() => null);
}

async function fetchCosts<T extends CostSummary>(
  path: "/api/costs/me" | "/api/costs/tenant",
  days: number,
  getToken: AccessTokenGetter,
  validate: (value: unknown) => value is T,
): Promise<T> {
  const response = await fetchWithAuth(
    `${path}?days=${encodeURIComponent(String(days))}`,
    { headers: {} },
    getToken,
  ).catch((error: unknown) => {
    const message = error instanceof Error ? error.message : "Cost request failed";
    throw new CostApiError(message, 0, "costs_network_error");
  });
  const body = await readJson(response);
  const details = errorMessage(response.status, body);

  if (details.code === COSTS_UNSUPPORTED_CODE) {
    throw new CostApiError(details.message, response.status, details.code);
  }
  if (!response.ok) {
    throw new CostApiError(details.message, response.status, details.code);
  }
  if (!validate(body)) {
    throw invalidResponse("The cost response from the server was malformed.");
  }
  return body;
}

export function isCostsUnsupportedError(error: unknown): boolean {
  return error instanceof CostApiError
    && (
      error.status === 404
      || error.status === 405
      || error.status === 501
      || error.code === COSTS_UNSUPPORTED_CODE
    );
}

export function isCostsForbiddenError(error: unknown): boolean {
  return error instanceof CostApiError && error.status === 403;
}

export function fetchMyCosts(
  getToken: AccessTokenGetter,
  days = 7,
): Promise<UserCostSummary> {
  return fetchCosts("/api/costs/me", days, getToken, validateUserSummary);
}

export function fetchTenantCosts(
  getToken: AccessTokenGetter,
  days = 7,
): Promise<TenantCostSummary> {
  return fetchCosts("/api/costs/tenant", days, getToken, validateTenantSummary);
}
